import {
  BrandApplicationForBusiness,
  BrandCampaign,
  BrandDealSummary,
  BrandTask,
  getBrandCampaignDetail,
  getBrandTasksForBusiness,
  getCampaignApplications,
} from '../api/payment/brand-deal.api';

// Yêu cầu hợp tác phía DOANH NGHIỆP, chia theo chiều (sếp góp ý 07/10): việc mình GỬI tới creator
// (lời mời riêng) tách khỏi việc creator GỬI tới mình (đơn ứng tuyển vào sàn, bài nộp brief chờ duyệt).
// Backend chưa có danh sách gộp nhiều chiến dịch nên gom từ API theo từng chiến dịch đang chạy.

/** 1 lời mời doanh nghiệp đã gửi creator (deal tạo từ "Mời trực tiếp"). */
export interface SentInviteItem {
  campaign: BrandCampaign;
  deal: BrandDealSummary;
}

/** 1 đơn creator xin tham gia chiến dịch trên sàn, đang chờ doanh nghiệp duyệt. */
export interface ReceivedApplicationItem {
  campaign: BrandCampaign;
  application: BrandApplicationForBusiness;
}

/** 1 bài nộp của creator cho brief sáng tạo, đang chờ doanh nghiệp duyệt. */
export interface ReceivedSubmissionItem {
  campaign: BrandCampaign;
  task: BrandTask;
}

export interface BusinessRequests {
  sentInvites: SentInviteItem[];
  receivedApplications: ReceivedApplicationItem[];
  receivedSubmissions: ReceivedSubmissionItem[];
  /** Số chiến dịch tải lỗi — để báo người dùng danh sách có thể chưa đủ. */
  failedCampaigns: number;
}

export const EMPTY_BUSINESS_REQUESTS: BusinessRequests = {
  sentInvites: [],
  receivedApplications: [],
  receivedSubmissions: [],
  failedCampaigns: 0,
};

/** Chỉ chiến dịch đang chạy (đã duyệt) mới còn yêu cầu cần xử lý — tránh gọi API cho chiến dịch nháp/đã đóng. */
export const activeRequestCampaigns = (campaigns: BrandCampaign[]) => {
  const approved = campaigns.filter((c) => c.status === 'Approved');
  return {
    direct: approved.filter((c) => c.kind === 'Direct'),
    // Đơn nhận đề xuất cũng sinh bài nộp chờ duyệt (đề xuất được chọn vào thẳng hàng chờ duyệt).
    creative: approved.filter((c) => c.kind === 'Creative' || c.kind === 'Proposal'),
  };
};

/** Deal của chiến dịch trừ đi deal sinh ra khi duyệt đơn ứng tuyển — phần còn lại là lời mời doanh nghiệp tự gửi. */
export const pickSentInvites = (
  campaign: BrandCampaign,
  deals: BrandDealSummary[],
  applications: BrandApplicationForBusiness[]
): SentInviteItem[] => {
  const fromApplications = new Set(applications.map((a) => a.dealId).filter(Boolean));
  return deals.filter((d) => !fromApplications.has(d.id)).map((deal) => ({ campaign, deal }));
};

export const pickPendingApplications = (
  campaign: BrandCampaign,
  applications: BrandApplicationForBusiness[]
): ReceivedApplicationItem[] =>
  applications.filter((a) => a.status === 'Pending').map((application) => ({ campaign, application }));

export const pickPendingSubmissions = (campaign: BrandCampaign, tasks: BrandTask[]): ReceivedSubmissionItem[] =>
  tasks.filter((t) => t.status === 'Submitted').map((task) => ({ campaign, task }));

const byNewest = (a?: string | null, b?: string | null) => (b || '').localeCompare(a || '');

/** Lời mời đang chờ creator trả lời lên đầu, còn lại giữ thứ tự theo chiến dịch. */
export const sortSentInvites = (items: SentInviteItem[]): SentInviteItem[] =>
  [...items].sort((a, b) => Number(b.deal.status === 'Invited') - Number(a.deal.status === 'Invited'));

/** Gom yêu cầu gửi đi và nhận được của mọi chiến dịch đang chạy (mỗi chiến dịch vài lượt gọi, chạy song song). */
export const loadBusinessRequests = async (campaigns: BrandCampaign[]): Promise<BusinessRequests> => {
  const { direct, creative } = activeRequestCampaigns(campaigns);
  const result: BusinessRequests = { sentInvites: [], receivedApplications: [], receivedSubmissions: [], failedCampaigns: 0 };

  const directJobs = direct.map(async (c) => {
    // Chưa từng mở sàn thì không có đơn ứng tuyển, khỏi gọi.
    const [detail, applications] = await Promise.all([
      getBrandCampaignDetail(c.id),
      c.listingStatus === 'None' ? Promise.resolve([] as BrandApplicationForBusiness[]) : getCampaignApplications(c.id),
    ]);
    return { c, detail, applications };
  });
  const creativeJobs = creative.map(async (c) => ({ c, tasks: await getBrandTasksForBusiness(c.id) }));

  const [directResults, creativeResults] = await Promise.all([
    Promise.allSettled(directJobs),
    Promise.allSettled(creativeJobs),
  ]);

  directResults.forEach((r) => {
    if (r.status !== 'fulfilled') {
      result.failedCampaigns += 1;
      return;
    }
    const { c, detail, applications } = r.value;
    result.sentInvites.push(...pickSentInvites(c, detail.deals || [], applications || []));
    result.receivedApplications.push(...pickPendingApplications(c, applications || []));
  });
  creativeResults.forEach((r) => {
    if (r.status !== 'fulfilled') {
      result.failedCampaigns += 1;
      return;
    }
    result.receivedSubmissions.push(...pickPendingSubmissions(r.value.c, r.value.tasks || []));
  });

  result.sentInvites = sortSentInvites(result.sentInvites);
  result.receivedApplications.sort((a, b) => byNewest(a.application.createdAt, b.application.createdAt));
  // Bài nộp sắp hết hạn duyệt lên trước.
  result.receivedSubmissions.sort((a, b) =>
    (a.task.reviewDueAt || '9999').localeCompare(b.task.reviewDueAt || '9999')
  );
  return result;
};
